import { API_BASE_URL } from '../services/api';

/**
 * Ensures an image URL is absolute by prepending API_BASE_URL if it's a relative path
 * (starts with /media/ or /static/)
 * 
 * @param {string} url - The image URL to process
 * @returns {string} - The absolute URL or the original URL
 */
export const getFullImageUrl = (url) => {
    if (!url || typeof url !== 'string') return url;

    // If the URL is absolute but contains /media/ or /static/, convert it to a relative path
    // so we can properly prepend the correct backend API host and port scheme
    if (url.includes('/media/') || url.includes('/static/')) {
        const mediaIndex = url.indexOf('/media/');
        const staticIndex = url.indexOf('/static/');
        if (mediaIndex !== -1 && url.substring(0, mediaIndex).startsWith('http')) {
            url = url.substring(mediaIndex);
        } else if (staticIndex !== -1 && url.substring(0, staticIndex).startsWith('http')) {
            url = url.substring(staticIndex);
        }
    }

    // If it's already an absolute URL (starts with http or data:), return it
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
        return url;
    }

    // If it's a relative path starting with /media/ or /static/, prepend API_BASE_URL
    // Note: media is usually served from the root, so if API_BASE_URL ends with /api, we strip it
    if (url.startsWith('/media/') || url.startsWith('/static/')) {
        let baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
        
        // Remove /api from the end of the base URL for media/static files
        if (baseUrl.endsWith('/api')) {
            baseUrl = baseUrl.slice(0, -4);
        }
        
        return `${baseUrl}${url}`;
    }

    // Standard frontend assets (starting with /assets/ or root icons)
    // No need to prepend API_BASE_URL as they are served from the frontend origin
    return url;
};

/**
 * Normalizes file and document URLs for downloading and viewing.
 * Converts absolute backend URLs (e.g. http://127.0.0.1/media/... or http://localhost:8000/api/...)
 * into origin-relative paths so the browser downloads them through the current frontend host and port.
 * 
 * @param {string} url - The file or document URL
 * @returns {string} - The normalized downloadable URL
 */
export const getFileUrl = (url) => {
    if (!url || typeof url !== 'string') return '';

    // If URL contains /media/, extract the relative /media/ path
    const mediaIdx = url.indexOf('/media/');
    if (mediaIdx !== -1) {
        return url.substring(mediaIdx);
    }

    // If URL contains /static/, extract the relative /static/ path
    const staticIdx = url.indexOf('/static/');
    if (staticIdx !== -1) {
        return url.substring(staticIdx);
    }

    // If URL contains /api/, extract the relative /api/ path
    const apiIdx = url.indexOf('/api/');
    if (apiIdx !== -1) {
        return url.substring(apiIdx);
    }

    return url;
};

